import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui';
import { PRO_TIER_NAME } from '../lib/productTier';

const CONTACT_EMAIL = 'support.peakperformanceiq@gmail.com';
const LAST_UPDATED = 'June 23, 2026';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-base font-semibold text-zinc-100">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-zinc-400">{children}</div>
    </section>
  );
}

export function PrivacyPage() {
  return (
    <div className="app-body min-h-screen">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <PageHeader
          title="Privacy Policy"
          subtitle={`Peak Performance IQ · Last updated ${LAST_UPDATED}`}
          compact={false}
        />

        <div className="glass-panel rounded-2xl p-5 sm:rounded-3xl sm:p-8">
          <p className="mb-8 text-sm leading-relaxed text-zinc-400">
            Peak Performance IQ (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates{' '}
            <a
              href="https://peakperformanceiq.com"
              className="text-sky-400 underline-offset-2 hover:underline"
            >
              peakperformanceiq.com
            </a>{' '}
            and related services. This policy explains what we collect, how we use it, and the
            choices you have.
          </p>

          <Section title="Information we collect">
            <p>
              <strong className="font-medium text-zinc-300">Account information:</strong> email
              address, password (stored securely hashed), and profile details you provide during
              onboarding (such as age, sex, height, weight, activity level, and training goals).
            </p>
            <p>
              <strong className="font-medium text-zinc-300">Health &amp; fitness data you log:</strong>{' '}
              nutrition entries, workouts, sleep times, body measurements, progress photos, goals,
              and notes you save in the app.
            </p>
            <p>
              <strong className="font-medium text-zinc-300">Payment information:</strong> if you
              subscribe to ad-free access or {PRO_TIER_NAME}, payments are processed by Apple (App
              Store) or Stripe (web). We do not store full card numbers on our servers.
            </p>
            <p>
              <strong className="font-medium text-zinc-300">Usage &amp; device data:</strong> basic
              technical logs (IP address, browser type, pages visited, and error logs) to keep the
              service secure and reliable.
            </p>
            <p>
              <strong className="font-medium text-zinc-300">Cookies &amp; similar technologies:</strong>{' '}
              we use cookies and local storage for sign-in sessions, theme preferences, and — if you
              consent — advertising and analytics as described below.
            </p>
          </Section>

          <Section title="How we use your information">
            <ul className="list-disc space-y-2 pl-5">
              <li>Provide, maintain, and improve Peak Performance IQ</li>
              <li>Calculate nutrition targets, track progress, and power {PRO_TIER_NAME} coaching features</li>
              <li>Process subscriptions and manage your account</li>
              <li>Send service-related communications (for example, password reset or billing)</li>
              <li>Detect abuse, fraud, and security issues</li>
              <li>Comply with legal obligations</li>
            </ul>
          </Section>

          <Section title="Advertising &amp; affiliate links">
            <p>
              We may show ads to users who have not purchased an ad-free or {PRO_TIER_NAME}
              subscription. We use <strong className="font-medium text-zinc-300">Google AdSense</strong>{' '}
              to display ads. Google and its partners may use cookies and similar technologies to
              serve and measure ads, including personalized ads where permitted and where you have
              given consent.
            </p>
            <p>
              Learn how Google uses data:{' '}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-400 underline-offset-2 hover:underline"
              >
                Google partner sites policy
              </a>
              . You can manage ad personalization at{' '}
              <a
                href="https://adssettings.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-400 underline-offset-2 hover:underline"
              >
                Google Ads Settings
              </a>
              .
            </p>
            <p>
              Some pages include <strong className="font-medium text-zinc-300">Amazon affiliate
              links</strong>. If you click and buy, we may earn a small commission at no extra cost
              to you. Amazon&apos;s privacy practices are governed by Amazon&apos;s own policies.
            </p>
          </Section>

          <Section title="European users (GDPR / UK / Switzerland)">
            <p>
              If you are in the European Economic Area, the United Kingdom, or Switzerland, we ask
              for your consent before using cookies or local storage for personalized advertising,
              where required by law. You can accept, reject, or customize choices through the
              consent banner shown on our site.
            </p>
            <p>
              Depending on where you live, you may have the right to access, correct, delete, or
              export your personal data, restrict or object to certain processing, and withdraw
              consent where processing is consent-based. Contact us to exercise these rights.
            </p>
          </Section>

          <Section title={PRO_TIER_NAME}>
            <p>
              When you use {PRO_TIER_NAME} coaching, relevant profile and logged data may be sent to our AI
              service providers to generate personalized responses. Do not enter information you are
              not comfortable sharing with those systems. AI output is for informational purposes
              and is not medical advice.
            </p>
          </Section>

          <Section title="How we share information">
            <p>We do not sell your personal information. We share data only with:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="font-medium text-zinc-300">Service providers</strong> (hosting,
                payments, email, AI, analytics, and advertising partners) who process data on our
                behalf
              </li>
              <li>
                <strong className="font-medium text-zinc-300">Legal requirements</strong> when
                required by law or to protect rights and safety
              </li>
              <li>
                <strong className="font-medium text-zinc-300">Business transfers</strong> in
                connection with a merger, acquisition, or asset sale
              </li>
            </ul>
          </Section>

          <Section title="Data retention">
            <p>
              We keep your account data while your account is active. You may request deletion by
              contacting us. Some data may be retained longer where required for legal, security, or
              backup purposes.
            </p>
          </Section>

          <Section title="Security">
            <p>
              We use industry-standard measures to protect your data, including encrypted
              connections (HTTPS) and secure password storage. No method of transmission or storage
              is 100% secure.
            </p>
          </Section>

          <Section title="Children">
            <p>
              Peak Performance IQ is not intended for children under 13 (or the minimum age required
              in your country). We do not knowingly collect personal information from children.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              We may update this policy from time to time. We will post the revised version on this
              page and update the &quot;Last updated&quot; date above.
            </p>
          </Section>

          <Section title="Contact us">
            <p>
              Questions about this policy or your data? Email us at{' '}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-sky-400 underline-offset-2 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>
        </div>

        <p className="mt-8 text-center text-sm text-zinc-500">
          <Link to="/terms" className="text-zinc-400 underline-offset-2 hover:text-zinc-300 hover:underline">
            Terms of Use
          </Link>
          <span className="mx-2 text-zinc-600">·</span>
          <Link to="/login" className="text-zinc-400 underline-offset-2 hover:text-zinc-300 hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
